import Keycloak, {
  KeycloakLoginOptions,
  KeycloakLogoutOptions,
  KeycloakProfile
} from "keycloak-js";
import { useCallback, useContext, useMemo } from "react";
import { TenantContext } from "../tenant";
import { ADMIN_ROLE, OPERATOR_ROLE } from "./roles";
import { KEYCLOAK_CLIENT_ID } from "./clients";
import { useAuthContext } from "./auth-provider";

export interface UseAuth {
  initialized: boolean;
  keycloak: Keycloak;
  authenticated: boolean | undefined;
  loadUserProfile: () => Promise<KeycloakProfile>;
  loadUserInfo: () => Promise<{}>;
  login: (options?: KeycloakLoginOptions) => Promise<void>;
  logout: (options?: KeycloakLogoutOptions) => Promise<void>;
  token?: string;
  hasRole: (role: string, clientId?: KEYCLOAK_CLIENT_ID) => boolean;
  hasAdminOrOperatorRole: () => boolean;

  getIsAuthenticated: () => boolean;
  getInitialized: () => boolean;
  getToken: () => string | undefined;
}

export const useAuth: () => UseAuth = () => {
  const { keycloak, initialized } = useAuthContext();
  const { keycloakConfig } = useContext(TenantContext);

  const hasRole = useCallback(
    (role: string, clientId?: KEYCLOAK_CLIENT_ID) => {
      return keycloak.hasResourceRole(role, clientId ? clientId : keycloakConfig.clientId);
    },
    [keycloak, keycloakConfig.clientId]
  );

  const hasAdminOrOperatorRole = useCallback(() => {
    return (
      hasRole(ADMIN_ROLE, KEYCLOAK_CLIENT_ID.PLATFORM) ||
      hasRole(OPERATOR_ROLE, KEYCLOAK_CLIENT_ID.PLATFORM)
    );
  }, [hasRole]);

  return useMemo(
    () => ({
      initialized,
      keycloak: keycloak,
      authenticated: keycloak.authenticated,
      getIsAuthenticated: () => !!keycloak.authenticated,
      getInitialized: () => initialized,
      getToken: () => keycloak.token,
      logout: keycloak.logout,
      login: keycloak.login,
      loadUserInfo: keycloak.loadUserInfo,
      loadUserProfile: keycloak.loadUserProfile,
      token: keycloak.token,
      hasRole,
      hasAdminOrOperatorRole
    }),
    [keycloak, initialized, hasRole, hasAdminOrOperatorRole]
  );
};
